import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { TrendingUp } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { roiDefaults } from "@/lib/home-content";
import { DiagnosticCta } from "@/components/DiagnosticCta";

const fmt = (n) =>
    n.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });

function Row({ label, value, min, max, step, onChange, display }) {
    return (
        <div>
            <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-[#5b5b66]">{label}</span>
                <span className="font-display text-base font-bold text-[#0a0a0b]">{display}</span>
            </div>
            <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} />
        </div>
    );
}

export default function ROICalculator() {
    const { ref, visible } = useReveal();
    const [leads, setLeads] = useState(roiDefaults.leads);
    const [dealSize, setDealSize] = useState(roiDefaults.dealSize);
    const [closeRate, setCloseRate] = useState(roiDefaults.closeRate);
    const lift = roiDefaults.lift;

    const { current, boosted, extraAnnual } = useMemo(() => {
        const cur = leads * (closeRate / 100) * dealSize;
        const boost = cur * (1 + lift / 100);
        return { current: cur, boosted: boost, extraAnnual: (boost - cur) * 12 };
    }, [leads, dealSize, closeRate, lift]);

    return (
        <section id="roi" className="bg-white px-5 py-14 sm:py-20">
            <div ref={ref} className={`reveal mx-auto max-w-3xl ${visible ? "is-visible" : ""}`}>
                <div className="mb-6 flex items-center justify-center gap-2 text-center">
                    <span className="rounded-full bg-[var(--brand-soft)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--brand)]">
                        ROI Calculator
                    </span>
                </div>
                <h2 className="text-center font-display text-3xl font-extrabold text-[#0a0a0b] sm:text-4xl">
                    See your revenue upside.
                </h2>

                <div className="mt-8 rounded-3xl bg-[#f7f8fc] p-5 shadow-[0_18px_50px_rgba(15,23,42,0.07)] ring-1 ring-black/5 sm:p-8">
                    <div className="space-y-6">
                        <Row
                            label="Monthly qualified leads"
                            value={leads}
                            min={10}
                            max={400}
                            step={5}
                            onChange={setLeads}
                            display={leads}
                        />
                        <Row
                            label="Average deal size"
                            value={dealSize}
                            min={500}
                            max={50000}
                            step={500}
                            onChange={setDealSize}
                            display={fmt(dealSize)}
                        />
                        <Row
                            label="Close rate"
                            value={closeRate}
                            min={1}
                            max={60}
                            step={1}
                            onChange={setCloseRate}
                            display={`${closeRate}%`}
                        />
                    </div>

                    <div className="mt-7 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-white p-4 ring-1 ring-black/5">
                            <div className="text-xs font-medium text-[#8a8a95]">Current monthly</div>
                            <div className="mt-1 font-display text-xl font-bold text-[#0a0a0b] sm:text-2xl">
                                {fmt(current)}
                            </div>
                        </div>
                        <div className="rounded-2xl bg-[var(--brand)] p-4 text-white shadow-[0_12px_28px_rgba(43,80,255,0.35)]">
                            <div className="flex items-center gap-1 text-xs font-medium text-white/80">
                                <TrendingUp className="h-3.5 w-3.5" /> With SalesCan +{lift}%
                            </div>
                            <div className="mt-1 font-display text-xl font-bold sm:text-2xl">{fmt(boosted)}</div>
                        </div>
                    </div>

                    <div className="mt-3 rounded-2xl border border-dashed border-[var(--brand)]/30 bg-[var(--brand-soft)] p-5 text-center">
                        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--brand)]">
                            Extra annual revenue
                        </div>
                        <div className="mt-1 font-display text-3xl font-extrabold text-[var(--brand)] sm:text-4xl">
                            {fmt(extraAnnual)}
                        </div>
                        <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-[#6b6b75]">
                            Illustrative only — a {lift}% lift is an assumption, not a verified SalesCan result.
                            The diagnostic maps the number against your actual funnel.
                        </p>
                        <DiagnosticCta className="mt-5" fullWidth testId="roi-cta" />
                    </div>
                </div>
            </div>
        </section>
    );
}
